import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Icon, Popover, Form } from '@anytag/shared-atoms';
import { TextFieldProps, TextSearchField } from '@anytag/shared-molecules';
import { InfluencerSearchKeywordField } from '@anytag/graphql';
import { IconNames } from '@anytag/shared-assets';
import { THEME } from '@anytag/shared-constants';
import { useOnClickOutside, useToggle } from '@anytag/shared-hooks';
import { DropdownOption } from '@anytag/shared-types';
import { cn } from '@anytag/shared-utils';
import { InfluencersFilterFormKeys, InfluencersFilterFormValues } from '../schemaTypes';

interface KeywordFieldProps extends Omit<TextFieldProps, 'name' | 'onFocus'> {
  searchByCategories?: boolean;
}

export const KeywordField = ({ className, searchByCategories, onSubmit, ...restProps }: KeywordFieldProps) => {
  const { t } = useTranslation();
  const { on: isOpening, setOn: openPopover, setOff: closePopover } = useToggle(false);
  const { watch, setValue } = useFormContext<InfluencersFilterFormValues>();

  const handleCloseSubmit = () => {
    onSubmit?.();
    closePopover();
  };

  const { ref } = useOnClickOutside<HTMLDivElement>(isOpening ? handleCloseSubmit : () => undefined);
  const [keywordValue, keywordFieldValue] = watch(['keyword', 'keywordField']);

  const categories: { value: InfluencerSearchKeywordField | ''; icon: IconNames; label: string }[] = [
    { value: '', icon: 'search', label: 'influencersSearchAllCategories' },
    { value: InfluencerSearchKeywordField.NAME, icon: 'user', label: 'searchByInfluencerName' },
    { value: InfluencerSearchKeywordField.SOCIAL_USERNAME, icon: 'user', label: 'searchByUserName' },
    { value: InfluencerSearchKeywordField.BIO, icon: 'user', label: 'searchByBio' },
    { value: InfluencerSearchKeywordField.PROFILE, icon: 'user', label: 'searchByProfile' },
    { value: InfluencerSearchKeywordField.TAG, icon: 'tag', label: 'searchByTag' },
    { value: InfluencerSearchKeywordField.EMAIL, icon: 'mail', label: 'searchByEmail' }
  ];
  const options: DropdownOption<string>[] = categories.map((att) => ({
    value: att.value,
    active: !!keywordFieldValue && att.value === keywordFieldValue,
    label: (
      <div css={{ display: 'flex', alignItems: 'center' }}>
        <Icon icon={att.icon} size="16px" variant="light-gray" />
        <p css={{ marginLeft: '8px', fontWeight: 600 }}>{keywordValue}</p>
        <p css={{ marginLeft: '16px', color: THEME.text.colors.gray.lv3 }}>{t(att.label)}</p>
      </div>
    )
  }));

  const handleSelectOption = ({ value }: DropdownOption<string, ReactNode>) => {
    setValue('keywordField', value as InfluencerSearchKeywordField);
    setTimeout(handleCloseSubmit, 0);
  };

  return (
    <Form.Field
      ref={ref}
      className={cn(
        'static flex-1',
        // Custom popover size
        '[&_[data-radix-popper-content-wrapper]]:mt-1 [&_[data-radix-popper-content-wrapper]]:w-[calc(100%-var(--filter-gap-x)*2)]',
        // Custom popover position
        '[&_[data-radix-popper-content-wrapper]]:absolute! [&_[data-radix-popper-content-wrapper]]:top-[unset]! [&_[data-radix-popper-content-wrapper]]:left-[calc(var(--filter-gap-x))]! [&_[data-radix-popper-content-wrapper]]:transform-[unset]!',
        className
      )}
    >
      <Popover
        withinPortal={false}
        open={!!(isOpening && keywordValue && searchByCategories)}
        contentProps={{
          onOpenAutoFocus: (e) => e.preventDefault() // Prevent auto focus to Popover when open, it cause input lose focus
        }}
        target={
          <TextSearchField<InfluencersFilterFormKeys>
            placeholder={t<string>('InfluencerFilter')}
            {...restProps}
            name="keyword"
            className="p-0!"
            onBlur={() => undefined} // Prevent submit onBlur
            onSubmit={handleCloseSubmit}
            onChange={(field, value) => {
              setValue(field, value);
              if (keywordFieldValue) {
                setValue('keywordField', '');
              }
            }}
            autoComplete={searchByCategories ? 'off' : 'on'}
            onFocus={searchByCategories ? openPopover : undefined}
          />
        }
      >
        {() => (
          <div className="box-border p-2">
            {options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelectOption(option)}
                className="transition-bg-color white box-border flex cursor-pointer items-center p-2.5 text-sm hover:bg-menu-item-hover"
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </Popover>
    </Form.Field>
  );
};
